import React, { useMemo, useState } from "react";
import {
  PiArrowRight,
  PiCaretDown,
  PiDownloadSimple,
  PiLock,
  PiPhoneIncoming,
  PiPhoneOutgoing,
  PiCheck,
  PiSealCheck,
  PiHourglassMedium,
} from "react-icons/pi";
import { Trans, useTranslation } from "react-i18next";
import { useTrainingNav, useTrainingQuery, useTraining, useDocumentTitle } from "../context";
import {
  Button,
  Pill,
  TensionDots,
  PageSkeleton,
  ErrorState,
  SectionHead,
  Empty,
  useCountUp,
} from "../components/ui";
import Seal from "../components/Seal";
import { LEVEL_STATUS, PALIER_NAMES, TARGET_STATUS, scoreTone } from "../constants";
import {
  cap,
  daysUntil,
  fmtDate,
  fmtDateLong,
  fmtDuration,
  levelLabel,
  levelTitle,
  tn,
  unitOf,
} from "../utils";
import { downloadCertificate } from "../certificate/makeCertificatePdf";
import "./agent.css";

const sealState = (status) =>
  ({
    locked: "locked",
    in_progress: "active",
    ready_for_validation: "ready",
    validated: "validated",
  })[status] || "active";
const nextPlayable = (level) =>
  level?.scenarios.find((s) => !s.lock && (s.best_score ?? -1) < level.pass_score);

/* ── hero: current level + difficulty rail ───────────────────────────────── */
const Hero = ({ level, onPlay, onOpen, unit }) => {
  const { t } = useTranslation();
  const next = nextPlayable(level);
  const ready = level.status === "ready_for_validation";
  return (
    <div>
      <div className="t-label rv">{t("{{unit}} en cours", { unit: cap(unit.one) })}</div>
      <div
        className="hero-level mt-4 rv"
        style={{
          "--i": 1,
        }}
      >
        <Seal
          level={level.level}
          numeral={!unit.theme}
          state={sealState(level.status)}
          size={148}
          className="hero-level__seal"
        />
        <div className="min-w-0">
          <h1
            className="t-display m-0"
            style={{
              fontSize: "clamp(2rem, 3.6vw, 3rem)",
            }}
          >
            {level.title}
          </h1>
          <ul className="skills-line">
            {level.skills?.map((s) => (
              <li key={s}>{s}</li>
            ))}
          </ul>
        </div>
      </div>

      <div
        className="rail rv"
        style={{
          "--i": 2,
        }}
        role="list"
        aria-label={
          unit.theme ? t("Niveaux du thème") : t("Paliers du {{unit}}", { unit: unit.one })
        }
      >
        {level.scenarios.map((s, i) => {
          const passed = (s.best_score ?? -1) >= level.pass_score;
          const state = passed
            ? "passed"
            : s.lock
            ? "locked"
            : s.id === next?.id
            ? "next"
            : s.best_score != null
            ? "failed"
            : "locked";
          return (
            <div
              key={s.id}
              className="rail__step"
              data-state={state}
              data-done={passed}
              role="listitem"
            >
              <button
                type="button"
                className="rail__node"
                disabled={!!s.lock}
                onClick={() => onOpen(s)}
                aria-label={
                  unit.theme
                    ? t("Niveau {{order}}, {{title}}", {
                        order: s.order,
                        title: s.title,
                      })
                    : t("Palier {{order}}, {{title}}", {
                        order: s.order,
                        title: s.title,
                      })
                }
              >
                {passed ? <PiCheck size={18} /> : s.lock ? <PiLock size={15} /> : s.order}
              </button>
              <div className="rail__name">{PALIER_NAMES[s.order - 1]}</div>
              <div className="rail__score tnum">{s.best_score != null ? s.best_score : "—"}</div>
            </div>
          );
        })}
      </div>

      <div
        className="mt-8 flex flex-wrap items-center gap-3 rv"
        style={{
          "--i": 3,
        }}
      >
        {next && (
          <Button
            variant="primary"
            size="lg"
            iconRight={PiArrowRight}
            onClick={() => onPlay(next)}
          >
            {next.attempts_count
              ? unit.theme
                ? t("Réessayer le niveau {{order}}", { order: next.order })
                : t("Réessayer le palier {{order}}", { order: next.order })
              : unit.theme
              ? t("Commencer le niveau {{order}}", { order: next.order })
              : t("Commencer le palier {{order}}", { order: next.order })}
          </Button>
        )}
        {next && (
          <Button
            variant="secondary"
            size="lg"
            onClick={() => onOpen(next)}
          >
            {t("Voir la fiche")}
          </Button>
        )}
        {ready && (
          <div className="flex items-center gap-3">
            <PiHourglassMedium
              size={22}
              color="var(--warn-ink)"
            />
            <div>
              <div className="font-semibold">
                {t("{{unit}} en attente de validation", { unit: cap(unit.one) })}
              </div>
              <div className="t-small">
                {t("Votre manager valide le {{unit}} et émet votre certificat.", {
                  unit: unit.one,
                })}
              </div>
            </div>
          </div>
        )}
      </div>
      {next && (
        <p
          className="t-small mt-4 mb-0 rv"
          style={{
            "--i": 4,
          }}
        >
          <Trans
            i18nKey="Prochain appel : <b>{{title}}</b> — {{name}}, {{organisation}}."
            values={{
              title: next.title,
              name: next.persona.name,
              organisation: next.persona.organisation,
            }}
            components={{ b: <strong className="text-[var(--ink)]" /> }}
          />
        </p>
      )}
    </div>
  );
};

/* ── ledger of numbers ───────────────────────────────────────────────── */
const DayStrip = ({ days = [] }) => {
  const { t } = useTranslation();
  const set = new Set(days);
  const cells = useMemo(() => {
    const out = [];
    for (let i = 13; i >= 0; i -= 1) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const k = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Paris",
      }).format(d);
      out.push({
        k,
        today: i === 0,
      });
    }
    return out;
  }, []);
  return (
    <div
      className="daystrip"
      role="img"
      aria-label={t("{{count}} jours d'activité sur les 14 derniers", {
        count: days.length,
      })}
    >
      {cells.map((c) => (
        <i
          key={c.k}
          data-on={set.has(c.k)}
          data-today={c.today}
        />
      ))}
    </div>
  );
};
const Ledger = ({ enrollment }) => {
  const { t } = useTranslation();
  const { stats, target, levels } = enrollment;
  const unit = unitOf(enrollment.program);
  const xp = useCountUp(stats.xp_total || 0, 1200);
  const avg = stats.avg_score;
  const left = daysUntil(target?.due_date);
  const ts = TARGET_STATUS[enrollment.target_status] || TARGET_STATUS.none;
  const todayDone = stats.activity_days?.includes(
    new Intl.DateTimeFormat("en-CA", {
      timeZone: "Europe/Paris",
    }).format(new Date())
  );
  return (
    <ul
      className="ledger rv"
      style={{
        "--i": 2,
      }}
    >
      <li>
        <span className="t-label">{t("Expérience")}</span>
        <span className="ledger__value">
          {xp.toLocaleString("fr-FR")}
          <span className="ledger__unit">{t("XP")}</span>
        </span>
      </li>
      <li>
        <span className="t-label">{t("Série")}</span>
        <span className="ledger__value">
          {stats.streak_live}
          <span className="ledger__unit">{stats.streak_live > 1 ? t("jours") : t("jour")}</span>
        </span>
        <div className="ledger__meta flex items-center justify-between gap-3">
          <DayStrip days={stats.activity_days} />
          <span>
            {todayDone
              ? t("Fait aujourd'hui")
              : stats.streak_live
              ? t("Un appel aujourd'hui la prolonge")
              : t("Lancez-en une")}
          </span>
        </div>
      </li>
      <li>
        <span className="t-label">{t("Score moyen")}</span>
        <span className="ledger__value">
          {avg != null ? String(avg).replace(".", ",") : "—"}
          <span className="ledger__unit">/ 100</span>
        </span>
        <div className="ledger__meta">
          {avg != null && avg >= (enrollment.program.levels[0]?.pass_score ?? 70)
            ? t("Seuil de réussite {{pass}} — vous êtes au-dessus", {
                pass: enrollment.program.levels[0]?.pass_score ?? 70,
              })
            : t("Seuil de réussite {{pass}}", {
                pass: enrollment.program.levels[0]?.pass_score ?? 70,
              })}
        </div>
      </li>
      <li>
        <span className="t-label">{t("Objectif du manager")}</span>
        {target?.level ? (
          <>
            <span className="ledger__value">{levelLabel(enrollment.program, target.level)}</span>
            <div className="ledger__meta flex items-center justify-between gap-3">
              <span
                className="ladder-mini"
                aria-hidden="true"
              >
                {Array.from({
                  length: 5,
                }).map((_, i) => (
                  <i
                    key={i}
                    data-s={levels[String(i + 1)]?.status}
                    data-target={target.level === i + 1}
                  />
                ))}
              </span>
              <span className="flex items-center gap-2">
                <Pill tone={ts.tone}>{ts.label}</Pill>
                <span className="tnum">
                  {left != null && left >= 0
                    ? t("{{date}} · J-{{left}}", {
                        date: fmtDate(target.due_date),
                        left,
                      })
                    : fmtDate(target.due_date)}
                </span>
              </span>
            </div>
          </>
        ) : (
          <span className="t-small">{t("Aucun objectif fixé")}</span>
        )}
      </li>
    </ul>
  );
};

/* ── one scenario row ────────────────────────────────────────────────── */
const ScenarioRow = ({ s, passScore, onOpen, onPlay, theme }) => {
  const { t } = useTranslation();
  const Dir = s.call_direction === "incoming" ? PiPhoneIncoming : PiPhoneOutgoing;
  const tone = scoreTone(s.best_score, passScore);
  const passed = (s.best_score ?? -1) >= passScore;
  return (
    <div
      className="srow"
      data-lock={!!s.lock}
    >
      <div className="srow__n">{s.order}</div>
      <div className="min-w-0">
        <div className="srow__title">{s.title}</div>
        <div className="srow__sub">
          {theme && (
            <>
              <strong>
                {s.is_certification
                  ? t("Niveau {{order}} · Certification", { order: s.order })
                  : t("Niveau {{order}}", { order: s.order })}
              </strong>
              {" · "}
            </>
          )}
          {s.persona.name} · {s.persona.organisation}
        </div>
      </div>
      <div className="flex items-center gap-2 t-small">
        <Dir
          size={16}
          aria-hidden="true"
        />
        {s.call_direction === "incoming" ? t("Entrant") : t("Sortant")}
      </div>
      <div>
        <TensionDots value={s.persona.tension} />
      </div>
      <div className="t-small tnum">~ {fmtDuration(s.estimated_duration_s)}</div>
      <div
        className="srow__score"
        style={{
          color:
            s.best_score == null
              ? "var(--ink-3)"
              : tone === "bad"
              ? "var(--bad-ink)"
              : tone === "warn"
              ? "var(--warn-ink)"
              : "var(--ink)",
        }}
        title={
          s.best_score != null
            ? t("Meilleur score : {{score}}", {
                score: s.best_score,
              })
            : undefined
        }
      >
        {s.best_score != null ? s.best_score : "—"}
      </div>
      <div className="flex justify-end gap-2">
        {s.lock ? (
          <span className="t-small flex items-center gap-2">
            <PiLock size={14} />{" "}
            {s.lock === "level"
              ? theme
                ? t("Thème verrouillé")
                : t("Niveau verrouillé")
              : theme
              ? t("Niveau précédent")
              : t("Palier précédent")}
          </span>
        ) : (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpen(s)}
            >
              {t("Fiche")}
            </Button>
            <Button
              variant={passed ? "secondary" : "primary"}
              size="sm"
              onClick={() => onPlay(s)}
            >
              {passed ? t("Rejouer") : s.attempts_count ? t("Réessayer") : t("Jouer")}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

/* ── level row ───────────────────────────────────────────────────────── */
const LevelRow = ({ level, open, onToggle, onOpenScenario, onPlay, previous, unit, program }) => {
  const { t } = useTranslation();
  const st = LEVEL_STATUS[level.status];
  const locked = level.status === "locked";
  return (
    <section
      className="level"
      data-open={open}
      data-status={level.status}
    >
      <button
        type="button"
        className="level__head"
        onClick={() => !locked && onToggle()}
        aria-expanded={open}
        aria-disabled={locked}
      >
        <Seal
          level={level.level}
          numeral={!unit.theme}
          state={sealState(level.status)}
          size={64}
        />
        <div className="min-w-0">
          {!unit.theme && (
            <div className="t-label mb-1">
              {t("{{unit}} {{level}}", {
                unit: cap(unit.one),
                level: unit.num(level.level),
              })}
            </div>
          )}
          <div className="level__title">{level.title}</div>
          <div className="t-small mt-1">
            {locked
              ? unit.theme
                ? t("Se débloque à la validation du thème « {{title}} » par votre manager", {
                    title: levelTitle(program, previous) || t("précédent"),
                  })
                : t("Se débloque à la validation du {{unit}} {{previous}} par votre manager", {
                    unit: unit.one,
                    previous: unit.num(previous),
                  })
              : level.best_avg != null
              ? t("{{passed}} / {{total}} appels réussis · moyenne {{avg}}", {
                  passed: level.passed_count,
                  total: level.total,
                  avg: String(level.best_avg).replace(".", ","),
                })
              : t("{{passed}} / {{total}} appels réussis", {
                  passed: level.passed_count,
                  total: level.total,
                })}
          </div>
        </div>
        <div className="level__status flex items-center gap-3">
          {level.status === "validated" && level.validated_at && (
            <span className="t-small">
              {t("Validé le {{date}}", {
                date: fmtDate(level.validated_at, {
                  year: true,
                }),
              })}
            </span>
          )}
          <Pill tone={st.tone}>{st.label}</Pill>
        </div>
        {locked ? (
          <PiLock
            size={18}
            color="var(--ink-3)"
          />
        ) : (
          <PiCaretDown
            className="level__chev"
            size={18}
          />
        )}
      </button>
      <div className="level__body">
        <div>
          <div className="srow-wrap">
            {level.scenarios.map((s) => (
              <ScenarioRow
                key={s.id}
                s={s}
                passScore={level.pass_score}
                theme={unit.theme}
                onOpen={onOpenScenario}
                onPlay={onPlay}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── page ────────────────────────────────────────────────────────────── */
const AgentHome = () => {
  const { t } = useTranslation();
  useDocumentTitle(t("Mon parcours"));
  const { go } = useTrainingNav();
  const { api } = useTraining();
  const me = useTrainingQuery((a) => a.getMe());
  const [progIdx, setProgIdx] = useState(0);
  const enrollment = me.data?.enrollments?.[progIdx];
  const path = useTrainingQuery(
    (a) => (enrollment ? a.getPath(enrollment.program.id) : null),
    [enrollment?.program?.id]
  );
  const [openLevel, setOpenLevel] = useState(null);
  if (me.error)
    return (
      <ErrorState
        error={me.error}
        onRetry={me.reload}
      />
    );
  if (!me.data || (enrollment && !path.data)) return <PageSkeleton />;
  if (!enrollment) {
    return (
      <Empty title={t("Aucune formation assignée")}>
        {t(
          "Aucune formation ne vous a été assignée pour le moment. Dès qu'un manager vous en assignera une, elle apparaîtra ici."
        )}
      </Empty>
    );
  }
  const { levels } = path.data;
  const unit = unitOf(enrollment.program);
  const current = levels.find((l) => l.level === enrollment.current_level) || levels[0];
  const expanded = openLevel ?? current.level;
  const play = (s) => go(`/live/${s.id}`);
  const open = (s) => go(`/scenario/${s.id}`);
  const certs = me.data.certificates;
  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 pb-8">
        <div className="t-small">
          {enrollment.program.name}
          {me.data.enrollments.length > 1 && (
            <select
              className="trn-select ml-3 !w-auto"
              value={progIdx}
              onChange={(e) => setProgIdx(Number(e.target.value))}
              aria-label={t("Parcours")}
            >
              {me.data.enrollments.map((e, i) => (
                <option
                  key={e.id}
                  value={i}
                >
                  {e.program.name}
                </option>
              ))}
            </select>
          )}
        </div>
        <div className="t-small">
          {me.data.operator.first_name} {me.data.operator.last_name} · {me.data.team?.name}
        </div>
      </div>

      <div className="agent-hero">
        <Hero
          level={current}
          onPlay={play}
          onOpen={open}
          unit={unit}
        />
        <Ledger enrollment={enrollment} />
      </div>

      <div className="mt-16">
        <SectionHead
          title={t("Le parcours")}
          aside={(() => {
            const n = levels.filter((l) => l.status === "validated").length;
            return n > 1
              ? t("{{count}} {{unit}} validés sur {{total}}", {
                  count: n,
                  unit: unit.many,
                  total: levels.length,
                })
              : t("{{count}} {{unit}} validé sur {{total}}", {
                  count: n,
                  unit: unit.one,
                  total: levels.length,
                });
          })()}
        />
        {levels.map((l) => (
          <LevelRow
            key={l.level}
            level={l}
            previous={l.level - 1}
            unit={unit}
            program={enrollment.program}
            open={expanded === l.level && l.status !== "locked"}
            onToggle={() => setOpenLevel(expanded === l.level ? -1 : l.level)}
            onOpenScenario={open}
            onPlay={play}
          />
        ))}
      </div>

      <div className="mt-16 max-w-[760px]">
        <SectionHead
          title={t("Certificats")}
          aside={
            certs.length
              ? tn(t, certs.length, "{{count}} certificat", "{{count}} certificats")
              : null
          }
        />
        {certs.length === 0 ? (
          <p className="t-small m-0">
            {t(
              "Un certificat est émis à chaque {{unit}} validé par votre manager. Le premier arrive dès le premier {{unit}} validé.",
              { unit: unit.one }
            )}
          </p>
        ) : (
          certs
            .slice()
            .sort((a, b) => b.level - a.level)
            .map((c) => (
              <div
                key={c.id}
                className="cert-row"
              >
                <Seal
                  level={c.level}
                  numeral={!unitOf({ unit: c.snapshot.unit }).theme}
                  state="validated"
                  size={52}
                />
                <div className="min-w-0">
                  <div className="font-semibold">
                    {unitOf({ unit: c.snapshot.unit }).theme
                      ? c.snapshot.level_title
                      : t("{{unit}} {{level}} — {{title}}", {
                          unit: cap(unitOf({ unit: c.snapshot.unit }).one),
                          level: unitOf({ unit: c.snapshot.unit }).num(c.level),
                          title: c.snapshot.level_title,
                        })}
                  </div>
                  <div className="t-small tnum">
                    {t("{{number}} · émis le {{date}} · moyenne {{value}}", {
                      number: c.number,
                      date: fmtDateLong(c.issued_at),
                      value: String(c.score_avg).replace(".", ","),
                    })}
                  </div>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  icon={PiDownloadSimple}
                  onClick={() => downloadCertificate(c)}
                >
                  {t("PDF")}
                </Button>
              </div>
            ))
        )}
      </div>
    </div>
  );
};
export default AgentHome;
